<script>
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import Pagination from '../components/Pagination.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);
  let pagination = $state(null);
  let includesRevenue = $state(false);
  let page = $state(1);
  let upcomingOnly = $state(false);

  async function load() {
    loading = true;
    error = null;
    try {
      const result = await api.listReservations({
        page, limit: 25,
        tenant_id: authStore.tenantId ?? undefined,
        upcoming_only: upcomingOnly || undefined,
      });
      items = result.items;
      pagination = result.pagination;
      includesRevenue = result.includes_revenue;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { page; upcomingOnly; load(); });

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  const money = (v, ccy) =>
    v == null ? '—' : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${ccy ?? ''}`.trim();

  // Revenue columns are appended only when the server says it sent them. The
  // API omits the fields entirely for property owners, so this is presentation
  // following the server's decision, never the enforcement point.
  const columns = $derived([
    { key: 'guest_name', label: 'Guest', render: (r) => r.guest_name ?? '—' },
    { key: 'property_name', label: 'Property', render: (r) => r.property_name ?? '—' },
    { key: 'arrival_date', label: 'Arrives', render: (r) => fmtDate(r.arrival_date) },
    { key: 'departure_date', label: 'Departs', render: (r) => fmtDate(r.departure_date) },
    { key: 'number_of_guests', label: 'Guests', align: 'right', render: (r) => r.number_of_guests ?? '—' },
    { key: 'status', label: 'Status', render: (r) => r.status ?? '—' },
    { key: 'door_code', label: 'Door code', render: (r) => r.door_code ?? '—' },
    ...(includesRevenue
      ? [{ key: 'total_price', label: 'Total', align: 'right',
           render: (r) => money(r.total_price, r.currency) }]
      : []),
  ]);
</script>

<PageHeader title="Reservations" subtitle="Bookings for your properties.">
  {#snippet children()}
    <label class="flex items-center gap-2 text-xs p-muted">
      <input type="checkbox" bind:checked={upcomingOnly} />
      Current &amp; upcoming
    </label>
  {/snippet}
</PageHeader>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if !loading && !items.length}
  <EmptyState icon="fa-calendar-days" title="No reservations"
              message="Bookings appear here once they sync from Hostaway." />
{:else}
  <DataTable {columns} rows={items} {loading} />
  <Pagination {pagination} onPage={(p) => (page = p)} />
{/if}
